import { useEffect, useState } from "react";
import api from "../lib/api";
import { Modal, Field } from "./Orders";
import { formatCurrency, formatDate } from "../lib/format";
import { Plus, PencilSimple, TrashSimple, GraduationCap } from "@phosphor-icons/react";

const empty = {
    name: "",
    father_name: "",
    mobile: "",
    address: "",
    course: "",
    batch: "",
    fees: 0,
    paid: 0,
    joining_date: "",
    photo_url: "",
    id_proof_url: "",
    status: "active",
};

export default function Students() {
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editId, setEditId] = useState(null);
    const [form, setForm] = useState(empty);
    const [filterStatus, setFilterStatus] = useState("");
    const [q, setQ] = useState("");

    const load = async () => {
        setLoading(true);
        const params = {};
        if (filterStatus) params.status = filterStatus;
        if (q) params.q = q;
        const { data } = await api.get("/students", { params });
        setStudents(data);
        setLoading(false);
    };
    useEffect(() => {
        load();
        // eslint-disable-next-line
    }, [filterStatus, q]);

    const onOpen = (s) => {
        if (s) {
            setEditId(s.id);
            setForm({ ...empty, ...s });
        } else {
            setEditId(null);
            setForm(empty);
        }
        setShowModal(true);
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        const payload = {
            ...form,
            fees: Number(form.fees) || 0,
            paid: Number(form.paid) || 0,
        };
        if (editId) await api.put(`/students/${editId}`, payload);
        else await api.post("/students", payload);
        setShowModal(false);
        load();
    };

    const onDelete = async (id) => {
        if (!window.confirm("Delete this student?")) return;
        await api.delete(`/students/${id}`);
        load();
    };

    return (
        <div className="space-y-6" data-testid="students-page">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <div className="label-uppercase">Coaching</div>
                    <h1 className="font-display text-3xl font-bold tracking-tight mt-1">
                        Students
                    </h1>
                    <p className="text-slate-400 text-sm mt-1">
                        Admissions, batches and fee tracking.
                    </p>
                </div>
                <button
                    data-testid="new-student-btn"
                    onClick={() => onOpen(null)}
                    className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 px-4 py-2.5 rounded-xl font-medium btn-glow"
                >
                    <Plus weight="bold" size={16} /> New Admission
                </button>
            </div>

            <div className="glass-card p-4 flex flex-wrap gap-3 items-center">
                <input
                    placeholder="Search by name / enrolment / mobile…"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm flex-1 min-w-[200px] focus:outline-none focus:border-blue-500/60"
                    data-testid="students-search"
                />
                <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500/60"
                >
                    <option value="">All statuses</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="completed">Completed</option>
                </select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {students.map((s) => (
                    <div
                        key={s.id}
                        className="glass-card p-5 animate-fade-up"
                        data-testid={`student-card-${s.id}`}
                    >
                        <div className="flex items-start gap-4">
                            <div className="h-14 w-14 rounded-xl bg-gradient-to-br from-blue-500 to-violet-500 grid place-items-center font-display font-bold text-lg shrink-0">
                                {(s.name || "?")[0].toUpperCase()}
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="font-display text-lg font-semibold truncate">
                                    {s.name}
                                </div>
                                <div className="font-mono text-[10px] text-slate-500">
                                    {s.enrolment_no}
                                </div>
                                <div className="text-xs text-slate-400 mt-1">
                                    {s.course}
                                    {s.batch && (
                                        <span className="text-slate-500"> · {s.batch}</span>
                                    )}
                                </div>
                            </div>
                            <span
                                className={`status-pill border ${
                                    s.status === "active"
                                        ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
                                        : s.status === "completed"
                                          ? "bg-blue-500/15 text-blue-300 border-blue-500/30"
                                          : "bg-slate-500/15 text-slate-300 border-slate-500/30"
                                }`}
                            >
                                {s.status}
                            </span>
                        </div>

                        <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                            <div className="p-2 rounded-lg bg-white/[0.03] border border-white/5">
                                <div className="text-[10px] uppercase tracking-widest text-slate-500">
                                    Fees
                                </div>
                                <div className="font-display font-bold text-xs mt-1">
                                    {formatCurrency(s.fees)}
                                </div>
                            </div>
                            <div className="p-2 rounded-lg bg-white/[0.03] border border-white/5">
                                <div className="text-[10px] uppercase tracking-widest text-slate-500">
                                    Paid
                                </div>
                                <div className="font-display font-bold text-xs mt-1 text-emerald-300">
                                    {formatCurrency(s.paid)}
                                </div>
                            </div>
                            <div className="p-2 rounded-lg bg-white/[0.03] border border-white/5">
                                <div className="text-[10px] uppercase tracking-widest text-slate-500">
                                    Due
                                </div>
                                <div className="font-display font-bold text-xs mt-1 text-rose-300">
                                    {formatCurrency(s.remaining)}
                                </div>
                            </div>
                        </div>

                        <div className="mt-4 flex items-center justify-between">
                            <div className="text-xs text-slate-500">
                                Joined: {formatDate(s.joining_date)}
                            </div>
                            <div className="flex gap-1">
                                <button
                                    onClick={() => onOpen(s)}
                                    className="p-2 rounded-lg hover:bg-white/5 text-slate-400 hover:text-blue-300"
                                    data-testid={`edit-student-${s.id}`}
                                >
                                    <PencilSimple size={14} />
                                </button>
                                <button
                                    onClick={() => onDelete(s.id)}
                                    className="p-2 rounded-lg hover:bg-white/5 text-slate-400 hover:text-rose-400"
                                >
                                    <TrashSimple size={14} />
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
                {!loading && students.length === 0 && (
                    <div className="md:col-span-2 lg:col-span-3 glass-card p-12 text-center text-slate-500">
                        <GraduationCap size={32} className="mx-auto mb-2 opacity-50" />
                        No students yet. Click "New Admission" to add the first one.
                    </div>
                )}
            </div>

            {showModal && (
                <Modal
                    title={editId ? "Edit Student" : "New Admission"}
                    onClose={() => setShowModal(false)}
                >
                    <form onSubmit={onSubmit} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Field label="Student Name" required>
                                <input
                                    required
                                    className="input"
                                    value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    data-testid="student-name"
                                />
                            </Field>
                            <Field label="Father Name">
                                <input
                                    className="input"
                                    value={form.father_name}
                                    onChange={(e) =>
                                        setForm({ ...form, father_name: e.target.value })
                                    }
                                />
                            </Field>
                            <Field label="Mobile" required>
                                <input
                                    required
                                    className="input"
                                    value={form.mobile}
                                    onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                                    data-testid="student-mobile"
                                />
                            </Field>
                            <Field label="Address">
                                <input
                                    className="input"
                                    value={form.address}
                                    onChange={(e) =>
                                        setForm({ ...form, address: e.target.value })
                                    }
                                />
                            </Field>
                            <Field label="Course" required>
                                <input
                                    required
                                    className="input"
                                    value={form.course}
                                    onChange={(e) => setForm({ ...form, course: e.target.value })}
                                    data-testid="student-course"
                                />
                            </Field>
                            <Field label="Batch / Timing">
                                <input
                                    className="input"
                                    value={form.batch}
                                    onChange={(e) => setForm({ ...form, batch: e.target.value })}
                                />
                            </Field>
                            <Field label="Total Fees (₹)" required>
                                <input
                                    type="number"
                                    required
                                    className="input"
                                    value={form.fees}
                                    onChange={(e) => setForm({ ...form, fees: e.target.value })}
                                    data-testid="student-fees"
                                />
                            </Field>
                            <Field label="Paid Amount (₹)">
                                <input
                                    type="number"
                                    className="input"
                                    data-testid="student-paid"
                                    value={form.paid}
                                    onChange={(e) => setForm({ ...form, paid: e.target.value })}
                                />
                            </Field>
                            <Field label="Joining Date">
                                <input
                                    type="date"
                                    className="input"
                                    value={form.joining_date || ""}
                                    onChange={(e) =>
                                        setForm({ ...form, joining_date: e.target.value })
                                    }
                                />
                            </Field>
                            <Field label="Status">
                                <select
                                    className="input"
                                    value={form.status}
                                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                                >
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                    <option value="completed">Completed</option>
                                </select>
                            </Field>
                            <Field label="Photo URL">
                                <input
                                    className="input"
                                    value={form.photo_url}
                                    onChange={(e) =>
                                        setForm({ ...form, photo_url: e.target.value })
                                    }
                                />
                            </Field>
                            <Field label="ID Proof URL">
                                <input
                                    className="input"
                                    value={form.id_proof_url}
                                    onChange={(e) =>
                                        setForm({ ...form, id_proof_url: e.target.value })
                                    }
                                />
                            </Field>
                        </div>

                        <div className="flex justify-end gap-2 pt-2">
                            <button
                                type="button"
                                onClick={() => setShowModal(false)}
                                className="px-4 py-2 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 text-sm"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                data-testid="save-student"
                                className="px-5 py-2 rounded-xl bg-blue-600 hover:bg-blue-500 btn-glow font-medium text-sm"
                            >
                                {editId ? "Save Changes" : "Admit Student"}
                            </button>
                        </div>
                    </form>
                </Modal>
            )}
        </div>
    );
}
